import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt } from 'react-icons/fa';

interface AddressAutocompleteProps {
  value: string;
  onChange: (addressData: AddressData) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

export interface AddressData {
  address: string;      // Rue et numéro (ex: "50 rue nationale")
  postalCode?: string;  // Code postal (ex: "59000")
  city?: string;        // Ville (ex: "Lille")
}

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0 12px;
  padding-left: 24px;
  font-size: 0.75rem;
  height: 26px;
  border: 1px solid ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.border};
  border-radius: 4px;
  transition: border-color 0.2s ease;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ hasError, theme }) => hasError ? `${theme.colors.error}30` : `${theme.colors.primary}30`};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabledBackground};
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    height: 26px;
    padding: 0 12px;
    padding-left: 24px;
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 7px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primaryLight};
  font-size: 12px;
  pointer-events: none;
  z-index: 1;
  
  @media (max-width: 768px) {
    left: 7px;
    font-size: 12px;
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.inputBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SuggestionItem = styled.li`
  padding: 6px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryLight}20;
  }
  
  .suggestion-main {
    font-weight: 500;
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.text};
  }
  
  .suggestion-details {
    font-size: 0.7rem;
    color: ${({ theme }) => theme.colors.textLight};
    margin-top: 1px;
  }
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
`;

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Rechercher une adresse...',
  error,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout>();

  // Mettre à jour inputValue quand value change de l'extérieur
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const searchMapboxAPI = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      return;
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
        if (!accessToken) {
          console.error('Token Mapbox manquant. Vérifiez REACT_APP_MAPBOX_TOKEN dans .env');
          setSuggestions([]);
          setIsLoading(false);
          return;
        }
        
        // Recherche d'adresses en France avec autocomplétion
        const apiUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${accessToken}&country=fr&language=fr&limit=5&types=address,place&autocomplete=true`;

        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }
        
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          setSuggestions(data.features);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche d\'adresses:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    searchMapboxAPI(newValue);
  }, [searchMapboxAPI]);

  const extractAddressComponents = (feature: any): AddressData => {
    // Extraire les composants de l'adresse depuis les contextes Mapbox
    let address = '';
    let postalCode = '';
    let city = '';

    // Le texte principal (numéro + rue)
    if (feature.address && feature.text) {
      address = `${feature.address} ${feature.text}`;
    } else if (feature.text) {
      address = feature.text;
    }

    // Parcourir les contextes pour trouver le code postal et la ville
    if (feature.context) {
      feature.context.forEach((ctx: any) => {
        if (ctx.id.startsWith('postcode')) {
          postalCode = ctx.text;
        } else if (ctx.id.startsWith('place')) {
          city = ctx.text;
        }
      });
    }

    return {
      address,
      postalCode,
      city
    };
  };

  const handleSuggestionClick = useCallback((suggestion: any) => {
    const addressData = extractAddressComponents(suggestion);
    
    setInputValue(addressData.address);
    setSuggestions([]);
    
    onChange(addressData);
  }, [onChange]);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  return (
    <AutocompleteContainer>
      <InputWrapper>
        <Icon>
          <FaMapMarkerAlt />
        </Icon>
        <StyledInput
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          hasError={!!error}
        />
      </InputWrapper>
      
      {error && <ErrorText>{error}</ErrorText>}
      
      {suggestions.length > 0 && (
        <SuggestionsList>
          {suggestions.map((suggestion) => (
            <SuggestionItem
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="suggestion-main">{suggestion.text}</div>
              <div className="suggestion-details">{suggestion.place_name}</div>
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
      
      {isLoading && inputValue.length >= 3 && (
        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '4px' }}>
          Recherche en cours...
        </div>
      )}
    </AutocompleteContainer>
  );
};
